import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { Icon } from '../icon/icon';
import { BreadcrumbService } from './breadcrumb.service';

/**
 * Breadcrumbs (`app-breadcrumbs`) — the clickable view-path trail, rendered
 * once in the app shell above the router outlet. Trails derive from route
 * config (see BreadcrumbService); a page opts in by carrying
 * `data.breadcrumb` on its route.
 *
 * Renders nothing below two crumbs — that guard is the whole show/hide
 * policy: list pages (whose grid-page title already says where you are),
 * the home redirect, and error pages simply produce no trail.
 *
 * Visual language matches the file-manager's folder breadcrumb.
 */
@Component({
  selector: 'app-breadcrumbs',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, TranslocoDirective, Icon],
  template: `
    @if (crumbs().length > 1) {
      <nav
        *transloco="let t"
        [attr.aria-label]="t('breadcrumb.ariaLabel')"
        class="mb-3 flex items-center gap-1.5 text-[13px] text-ink-2"
      >
        @for (crumb of crumbs(); track crumb.url; let last = $last) {
          @if (!last) {
            <a
              [routerLink]="crumb.url"
              [title]="crumb.label"
              class="max-w-[240px] truncate font-semibold text-link hover:underline"
              >{{ crumb.label }}</a
            >
            <app-icon name="icon-arrow-right" [size]="16" class="shrink-0 text-ink-3" />
          } @else {
            <span
              aria-current="page"
              [title]="crumb.label"
              class="max-w-[320px] truncate font-semibold text-ink-1"
              >{{ crumb.label }}</span
            >
          }
        }
      </nav>
    }
  `,
})
export class Breadcrumbs {
  protected readonly crumbs = inject(BreadcrumbService).crumbs;
}
